import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CreditCardIcon, BanknoteIcon, WalletIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
const Payment = () => {
  const navigate = useNavigate();
  const {
    user,
    isLoggedIn
  } = useAuth();
  const [cart, setCart] = useState([]);
  const [checkoutDetails, setCheckoutDetails] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    cardHolder: '',
    expiryDate: '',
    cvv: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  // Load cart and checkout details from localStorage
  useEffect(() => {
    if (!isLoggedIn || !user) {
      navigate('/login');
      return;
    }
    const savedCart = localStorage.getItem(`cart_${user.email}`);
    const savedCheckoutDetails = localStorage.getItem(`checkoutDetails_${user.email}`);
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    } else {
      navigate('/checkout');
      return;
    }
    if (savedCheckoutDetails) {
      const details = JSON.parse(savedCheckoutDetails);
      setCheckoutDetails(details);
      // Set payment method based on checkout details
      if (details.paymentMethod === 'Credit/Debit Card') {
        setPaymentMethod('card');
      } else if (details.paymentMethod === 'GCash') {
        setPaymentMethod('wallet');
      } else {
        setPaymentMethod('cash');
      }
    } else {
      navigate('/checkout');
      return;
    }
    setLoading(false);
  }, [navigate, user, isLoggedIn]);
  const handleCardDetailsChange = e => {
    const {
      name,
      value
    } = e.target;
    setCardDetails({
      ...cardDetails,
      [name]: value
    });
  };
  const validateForm = () => {
    const newErrors = {};
    if (paymentMethod === 'card') {
      if (!cardDetails.cardNumber) {
        newErrors.cardNumber = 'Card number is required';
      } else if (!/^\d{16}$/.test(cardDetails.cardNumber.replace(/\s/g, ''))) {
        newErrors.cardNumber = 'Card number must be 16 digits';
      }
      if (!cardDetails.cardHolder) {
        newErrors.cardHolder = 'Card holder name is required';
      }
      if (!cardDetails.expiryDate) {
        newErrors.expiryDate = 'Expiry date is required';
      } else if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(cardDetails.expiryDate)) {
        newErrors.expiryDate = 'Expiry date must be in MM/YY format';
      }
      if (!cardDetails.cvv) {
        newErrors.cvv = 'CVV is required';
      } else if (!/^\d{3,4}$/.test(cardDetails.cvv)) {
        newErrors.cvv = 'CVV must be 3 or 4 digits';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handlePlaceOrder = e => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    // Generate an order number
    const orderNumber = 'ORD-' + Math.floor(100000 + Math.random() * 900000);
    // Determine payment method text
    let paymentMethodText = 'Cash on Delivery';
    if (paymentMethod === 'card') {
      paymentMethodText = 'Credit/Debit Card';
    } else if (paymentMethod === 'wallet') {
      paymentMethodText = 'GCash';
    }
    // Create order object
    const order = {
      id: orderNumber,
      date: new Date().toISOString(),
      items: cart,
      subtotal: checkoutDetails.subtotal,
      deliveryFee: checkoutDetails.deliveryFee,
      total: checkoutDetails.total,
      deliveryAddress: checkoutDetails.deliveryAddress,
      contactNumber: checkoutDetails.contactNumber,
      specialInstructions: checkoutDetails.specialInstructions,
      paymentMethod: paymentMethodText,
      status: 'Confirmed',
      customerEmail: user.email,
      customerName: user.name || 'Customer',
      timeline: [{
        status: 'Order Placed',
        time: new Date().toLocaleString()
      }, {
        status: 'Order Confirmed',
        time: new Date().toLocaleString()
      }]
    };
    // Save order to localStorage
    let orders = JSON.parse(localStorage.getItem('orders') || '[]');
    orders.push(order);
    localStorage.setItem('orders', JSON.stringify(orders));
    // Save order ID for confirmation page
    localStorage.setItem('currentOrder', orderNumber);
    // Clear cart and checkout details
    localStorage.removeItem(`cart_${user.email}`);
    localStorage.removeItem(`checkoutDetails_${user.email}`);
    // Navigate to confirmation page
    navigate('/order-confirmation');
  };
  if (loading) {
    return <div className="flex justify-center items-center min-h-screen bg-amber-50" data-id="element-1675">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-800" data-id="element-1676"></div>
        <p className="ml-3 text-amber-800" data-id="element-1677">Loading payment details...</p>
      </div>;
  }
  if (!checkoutDetails) {
    return <div className="bg-amber-50 min-h-screen py-8 flex justify-center items-center" data-id="element-1678">
        <div className="max-w-md mx-auto px-4 text-center" data-id="element-1679">
          <h2 className="text-2xl font-bold text-amber-900 mb-4" data-id="element-1680">
            No Checkout Details Found
          </h2>
          <p className="text-amber-800 mb-6" data-id="element-1681">
            Please complete the checkout process first.
          </p>
          <Link to="/checkout" className="px-4 py-2 bg-amber-800 text-white rounded-md hover:bg-amber-900" data-id="element-1682">
            Go to Checkout
          </Link>
        </div>
      </div>;
  }
  return <div className="bg-amber-50 min-h-screen py-8" data-id="element-1683">
      <div className="max-w-6xl mx-auto px-4" data-id="element-1684">
        <div className="mb-8" data-id="element-1685">
          <h1 className="text-3xl font-bold text-amber-900" data-id="element-1686">Payment</h1>
          <div className="flex items-center mt-2" data-id="element-1687">
            <div className="w-8 h-8 bg-amber-300 text-amber-900 rounded-full flex items-center justify-center" data-id="element-1688">
              ✓
            </div>
            <div className="h-1 w-24 bg-amber-800 mx-2" data-id="element-1689"></div>
            <div className="w-8 h-8 bg-amber-800 text-white rounded-full flex items-center justify-center" data-id="element-1690">
              2
            </div>
            <div className="h-1 w-24 bg-amber-300 mx-2" data-id="element-1691"></div>
            <div className="w-8 h-8 bg-amber-300 text-amber-900 rounded-full flex items-center justify-center" data-id="element-1692">
              3
            </div>
          </div>
          <div className="flex text-sm mt-1" data-id="element-1693">
            <span className="w-8 text-center" data-id="element-1694">Checkout</span>
            <span className="w-24 mx-2" data-id="element-1695"></span>
            <span className="w-8 text-center" data-id="element-1696">Payment</span>
            <span className="w-24 mx-2" data-id="element-1697"></span>
            <span className="w-8 text-center" data-id="element-1698">Confirm</span>
          </div>
        </div>
        <div className="flex flex-col lg:flex-row gap-8" data-id="element-1699">
          {/* Payment Form */}
          <div className="lg:w-2/3" data-id="element-1700">
            <div className="bg-white rounded-lg shadow-md p-6 mb-6" data-id="element-1701">
              <h2 className="text-xl font-semibold text-amber-900 mb-4" data-id="element-1702">
                Payment Method
              </h2>
              <form onSubmit={handlePlaceOrder} data-id="element-1703">
                <div className="space-y-4 mb-6" data-id="element-1704">
                  <label className={`flex p-4 border rounded-md cursor-pointer ${paymentMethod === 'cash' ? 'border-amber-800 bg-amber-50' : 'border-gray-300'}`} data-id="element-1705">
                    <input type="radio" name="paymentMethod" value="cash" checked={paymentMethod === 'cash'} onChange={() => setPaymentMethod('cash')} className="h-5 w-5 text-amber-600 focus:ring-amber-500 mt-1" data-id="element-1706" />
                    <div className="ml-3 flex-grow" data-id="element-1707">
                      <div className="flex justify-between" data-id="element-1708">
                        <span className="font-medium" data-id="element-1709">Cash on Delivery</span>
                        <BanknoteIcon className="h-5 w-5 text-amber-800" data-id="element-1710" />
                      </div>
                      <span className="block text-sm text-gray-600 mt-1" data-id="element-1711">
                        Pay with cash when your order arrives
                      </span>
                    </div>
                  </label>
                  <label className={`flex p-4 border rounded-md cursor-pointer ${paymentMethod === 'card' ? 'border-amber-800 bg-amber-50' : 'border-gray-300'}`} data-id="element-1712">
                    <input type="radio" name="paymentMethod" value="card" checked={paymentMethod === 'card'} onChange={() => setPaymentMethod('card')} className="h-5 w-5 text-amber-600 focus:ring-amber-500 mt-1" data-id="element-1713" />
                    <div className="ml-3 flex-grow" data-id="element-1714">
                      <div className="flex justify-between" data-id="element-1715">
                        <span className="font-medium" data-id="element-1716">Credit / Debit Card</span>
                        <CreditCardIcon className="h-5 w-5 text-amber-800" data-id="element-1717" />
                      </div>
                      <span className="block text-sm text-gray-600 mt-1" data-id="element-1718">
                        Pay securely with your card
                      </span>
                    </div>
                  </label>
                  <label className={`flex p-4 border rounded-md cursor-pointer ${paymentMethod === 'wallet' ? 'border-amber-800 bg-amber-50' : 'border-gray-300'}`} data-id="element-1719">
                    <input type="radio" name="paymentMethod" value="wallet" checked={paymentMethod === 'wallet'} onChange={() => setPaymentMethod('wallet')} className="h-5 w-5 text-amber-600 focus:ring-amber-500 mt-1" data-id="element-1720" />
                    <div className="ml-3 flex-grow" data-id="element-1721">
                      <div className="flex justify-between" data-id="element-1722">
                        <span className="font-medium" data-id="element-1723">Digital Wallet</span>
                        <WalletIcon className="h-5 w-5 text-amber-800" data-id="element-1724" />
                      </div>
                      <span className="block text-sm text-gray-600 mt-1" data-id="element-1725">
                        Pay with GCash, PayMaya, or other e-wallets
                      </span>
                    </div>
                  </label>
                </div>
                {paymentMethod === 'card' && <div className="border border-gray-200 rounded-md p-4 mb-6" data-id="element-1726">
                    <h3 className="text-md font-medium text-amber-800 mb-3" data-id="element-1727">
                      Card Details
                    </h3>
                    <div className="space-y-4" data-id="element-1728">
                      <div data-id="element-1729">
                        <label htmlFor="cardNumber" className="block text-sm font-medium text-amber-800 mb-1" data-id="element-1730">
                          Card Number
                        </label>
                        <input id="cardNumber" name="cardNumber" type="text" placeholder="1234 5678 9012 3456" className={`w-full px-3 py-2 border ${errors.cardNumber ? 'border-red-300' : 'border-amber-300'} rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500`} value={cardDetails.cardNumber} onChange={handleCardDetailsChange} data-id="element-1731" />
                        {errors.cardNumber && <p className="mt-1 text-sm text-red-600" data-id="element-1732">
                            {errors.cardNumber}
                          </p>}
                      </div>
                      <div data-id="element-1733">
                        <label htmlFor="cardHolder" className="block text-sm font-medium text-amber-800 mb-1" data-id="element-1734">
                          Card Holder Name
                        </label>
                        <input id="cardHolder" name="cardHolder" type="text" placeholder="John Doe" className={`w-full px-3 py-2 border ${errors.cardHolder ? 'border-red-300' : 'border-amber-300'} rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500`} value={cardDetails.cardHolder} onChange={handleCardDetailsChange} data-id="element-1735" />
                        {errors.cardHolder && <p className="mt-1 text-sm text-red-600" data-id="element-1736">
                            {errors.cardHolder}
                          </p>}
                      </div>
                      <div className="grid grid-cols-2 gap-4" data-id="element-1737">
                        <div data-id="element-1738">
                          <label htmlFor="expiryDate" className="block text-sm font-medium text-amber-800 mb-1" data-id="element-1739">
                            Expiry Date
                          </label>
                          <input id="expiryDate" name="expiryDate" type="text" placeholder="MM/YY" className={`w-full px-3 py-2 border ${errors.expiryDate ? 'border-red-300' : 'border-amber-300'} rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500`} value={cardDetails.expiryDate} onChange={handleCardDetailsChange} data-id="element-1740" />
                          {errors.expiryDate && <p className="mt-1 text-sm text-red-600" data-id="element-1741">
                              {errors.expiryDate}
                            </p>}
                        </div>
                        <div data-id="element-1742">
                          <label htmlFor="cvv" className="block text-sm font-medium text-amber-800 mb-1" data-id="element-1743">
                            CVV
                          </label>
                          <input id="cvv" name="cvv" type="text" placeholder="123" className={`w-full px-3 py-2 border ${errors.cvv ? 'border-red-300' : 'border-amber-300'} rounded-md shadow-sm focus:outline-none focus:ring-amber-500 focus:border-amber-500`} value={cardDetails.cvv} onChange={handleCardDetailsChange} data-id="element-1744" />
                          {errors.cvv && <p className="mt-1 text-sm text-red-600" data-id="element-1745">
                              {errors.cvv}
                            </p>}
                        </div>
                      </div>
                    </div>
                  </div>}
                <div className="mt-6 flex justify-between" data-id="element-1746">
                  <Link to="/checkout" className="inline-flex items-center px-4 py-2 border border-amber-800 text-amber-800 rounded-md hover:bg-amber-50" data-id="element-1747">
                    <ArrowLeftIcon className="h-4 w-4 mr-2" data-id="element-1748" />
                    Back to Checkout
                  </Link>
                  <button type="submit" className="px-6 py-2 bg-amber-800 text-white rounded-md hover:bg-amber-900" data-id="element-1749">
                    Place Order
                  </button>
                </div>
              </form>
            </div>
          </div>
          {/* Order Summary */}
          <div className="lg:w-1/3" data-id="element-1750">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-4" data-id="element-1751">
              <h2 className="text-xl font-semibold text-amber-900 mb-4" data-id="element-1752">
                Order Summary
              </h2>
              <div className="max-h-64 overflow-y-auto mb-4" data-id="element-1753">
                {cart.map((item, index) => <div key={index} className="flex justify-between items-center py-3 border-b border-gray-200" data-id="element-1754">
                    <div className="flex items-center" data-id="element-1755">
                      <span className="text-amber-800 font-medium" data-id="element-1756">
                        {item.quantity}x
                      </span>
                      <div className="ml-3" data-id="element-1757">
                        <h4 className="text-sm font-medium" data-id="element-1758">{item.name}</h4>
                      </div>
                    </div>
                    <span className="font-medium" data-id="element-1759">
                      ₱{(item.price * item.quantity).toFixed(2)}
                    </span>
                  </div>)}
              </div>
              <div className="space-y-2" data-id="element-1760">
                <div className="flex justify-between text-sm" data-id="element-1761">
                  <span className="text-gray-600" data-id="element-1762">Subtotal</span>
                  <span className="font-medium" data-id="element-1763">
                    ₱{checkoutDetails.subtotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-1764">
                  <span className="text-gray-600" data-id="element-1765">Delivery Fee</span>
                  <span className="font-medium" data-id="element-1766">
                    ₱{checkoutDetails.deliveryFee.toFixed(2)}
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-2 mt-2" data-id="element-1767"></div>
                <div className="flex justify-between font-semibold" data-id="element-1768">
                  <span data-id="element-1769">Total</span>
                  <span data-id="element-1770">₱{checkoutDetails.total.toFixed(2)}</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1771">
                <h3 className="text-md font-medium text-amber-800 mb-2" data-id="element-1772">
                  Delivery Details
                </h3>
                <p className="text-sm text-gray-600" data-id="element-1773">
                  {checkoutDetails.deliveryAddress}
                </p>
                <p className="text-sm text-gray-600 mt-1" data-id="element-1774">
                  Contact: {checkoutDetails.contactNumber}
                </p>
                {checkoutDetails.specialInstructions && <div className="mt-2" data-id="element-1775">
                    <span className="text-sm font-medium text-amber-800" data-id="element-1776">
                      Special Instructions:
                    </span>
                    <p className="text-sm text-gray-600 mt-1" data-id="element-1777">
                      {checkoutDetails.specialInstructions}
                    </p>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default Payment;